import { TableRow, TableCell, IconButton, FormControlLabel, Checkbox, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Box } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { CheckboxCell, ArrowCell, WorkStepCell } from '../../../style/tablecell';
import { SELECTED_DRAINAGE_POWERPLANT } from "../../../const/config";
import { DATA_ROW_RESET_FIELDS } from '../../../const/suiro_fields';
import { getCellBgColor } from '../../../func/util';
import { get } from 'react-hook-form';

/**
 * 水路抜水オプション発電所リストの単一行コンポーネント
 * @param {object} props
 * @param {object} props.row - 行データ
 * @param {object} props.lefts - 固定列の位置情報
 * @param {Array} props.users - ユーザーリスト
 * @param {Array} props.partners - 協力会社リスト
 * @param {function} props.handleReset - リセットハンドラ
 * @param {function} props.handleCheckboxChange - チェックボックス変更ハンドラ
 * @param {function} props.handleTextFieldChange - テキスト変更ハンドラ
 * @param {function} props.handleRowTimeChange - 行内の複数項目時刻変更ハンドラ
 * @param {function} props.handleRowSelectChange - 行内の複数項目選択変更ハンドラ
 */

export const OptionalDataRow = ({
  row,
  lefts,
  users,
  partners,
  handleReset,
  handleCheckboxChange,
  handleTextFieldChange,
  handleRowTimeChange,
  handleRowSelectChange
}) => {

  // 異常あり(true)なら false (無効化しない=有効)、異常なし(false)なら true (無効化する)
  const isDisabled = row.is_not_report;
  const isSelectedDrainageIntakestop = row.is_selected_drainage_powerplant === SELECTED_DRAINAGE_POWERPLANT.INTAKESTOP; // 取水停止
  const isSelectedDrainageDrainage = row.is_selected_drainage_powerplant === SELECTED_DRAINAGE_POWERPLANT.DRAINAGE; // 水路抜水
  const isSelectedDrainageContinue = row.is_selected_drainage_powerplant === SELECTED_DRAINAGE_POWERPLANT.CONTINUE; // 運転継続
  const isStepsAfterAbnormalDisabled = !row.is_emergency_inspection; // 臨時点検異常なし
  const isDrainageAbnormal = !(isSelectedDrainageDrainage || !isStepsAfterAbnormalDisabled); // 水路抜粋が選択されている or 臨時点検異常あり
  const filteredPartners = partners.filter(p => { return p.facility_uuid === row.uuid; });

  const CONTACT_FIELD_WIDTH = 190;

  return (
    <TableRow
      key={row.uuid}
      sx={{
        backgroundColor: (row.is_not_report || row.is_notReport) ? "#eeeeee" : (row.is_completed ? '#e8f5e9' : 'inherit'),
        alignItems: 'center',
        height: 160,
        '& .MuiTableCell-root': { height: 160 }
      }}
    >
      {/* 固定列: リセットボタン */}
      <TableCell
        sx={{
          width: 65,
          minWidth: 65,
          position: 'sticky',
          left: lefts.reset,
          zIndex: 900,
          backgroundColor: row.is_not_report ? "#eeeeee" : (row.is_completed ? '#e8f5e9' : 'background.paper'),
          borderBottom: '1px solid rgba(224,224,224,1)'
        }}
      >
        <IconButton
          size="small"
          onClick={() => handleReset(row.uuid, DATA_ROW_RESET_FIELDS)}
          title="この行のチェックをクリア"
          sx={{ p: 0.5 }}
        >
          <Refresh fontSize="small" />
        </IconButton>
      </TableCell>

      {/* 固定列: 報告対象外 */}
      <CheckboxCell
        sx={{
          width: 80,
          minWidth: 80,
          position: 'sticky',
          left: lefts.notReport,
          zIndex: 900,
          backgroundColor: row.is_not_report ? "#eeeeee" : (row.is_completed ? '#e8f5e9' : 'background.paper'),
          borderBottom: '1px solid rgba(224,224,224,1)'
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_not_report}
              onChange={() => handleCheckboxChange(row.uuid, 'is_not_report', false)}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      {/* 固定列: 発電所名 */}
      <WorkStepCell
        sx={{
          minWidth: 120,
          position: 'sticky',
          left: lefts.name,
          zIndex: 900,
          backgroundColor: row.is_not_report ? "#eeeeee" : (row.is_completed ? '#e8f5e9' : 'background.paper'),
          borderBottom: '1px solid rgba(224,224,224,1)',
          borderRight: '1px solid rgba(224,224,224,1)'
        }}
      >
        {row.name}
      </WorkStepCell>

      {/* スクロール領域: 地震発生日時 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.occured_at, isDisabled, false)}}>
        <TextField
          label='地震発生日時'
          variant="outlined"
          size="small"
          fullWidth
          placeholder='2025/11/07 10:00'
          value={row.occured_at || ''}
          onChange={(e) => handleTextFieldChange(row.uuid, e.target.value, 'occured_at', false)}
          disabled={isDisabled}
          sx={{
            '& .MuiInputBase-root': {
              height: 40,
              boxSizing: 'border-box'
            }
          }}
        />
      </TableCell>

      {/* 各発電所運転状況確認 - 空 */}
      <TableCell />
      <ArrowCell />

      {/* 水路抜水発電所を決定 */}
    <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_selected_drainage_powerplant, isDisabled, false)}}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={row.is_selected_drainage_powerplant === SELECTED_DRAINAGE_POWERPLANT.INTAKESTOP}
                onChange={() => handleCheckboxChange(row.uuid, 'is_selected_drainage_powerplant', false, 'INTAKESTOP')}
                disabled={isDisabled}
                size="small"
              />
            }
            label="取水停止"
            slotProps={{
              typography: { sx: { fontSize: '0.95rem' } }
            }}
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={row.is_selected_drainage_powerplant === SELECTED_DRAINAGE_POWERPLANT.DRAINAGE}
                onChange={() => handleCheckboxChange(row.uuid, 'is_selected_drainage_powerplant', false, 'DRAINAGE')}
                disabled={isDisabled}
                size="small"
              />
            }
            label="水路抜水"
            slotProps={{
              typography: { sx: { fontSize: '0.95rem' } }
            }}
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={row.is_selected_drainage_powerplant === SELECTED_DRAINAGE_POWERPLANT.CONTINUE}
                onChange={() => handleCheckboxChange(row.uuid, 'is_selected_drainage_powerplant', false, 'CONTINUE')}
                disabled={isDisabled}
                size="small"
              />
            }
            label="運転継続"
            slotProps={{
              typography: { sx: { fontSize: '0.95rem' } }
            }}
            sx={{ m: 0 }}
          />
        </Box>
      </CheckboxCell>

      <ArrowCell />
      {/* 取水停止・水路抜水発電所を電気グループへ共有 */}
      <TableCell />
      <ArrowCell />

      {/* 灌漑利水者へ連絡 (1回目) */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_notified_irrigation_user1, isDisabled, isSelectedDrainageContinue)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_notified_irrigation_user1}
              onChange={() => handleCheckboxChange(row.uuid, 'is_notified_irrigation_user1', false)}
              disabled={isDisabled || isSelectedDrainageContinue}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />
      {/* 取水停止(遠隔操作有)の実施及び総制への連絡 */}
      <TableCell />
      <ArrowCell />

      {/* 取水停止連絡 */}
      <TableCell sx ={{ minWidth: 180, maxWidth: 380, py: 0.5, backgroundColor: getCellBgColor(!!(row.intake_stop_at && row.intake_stop_notifier_person && row.intake_stop_contact_person), isDisabled, isSelectedDrainageContinue) }}>
        {(() => {
          const hasTime = !!row.intake_stop_at;
          const notifierSelected = !!row.intake_stop_notifier_person;
          const contactSelected = !!row.intake_stop_contact_person;
          // いずれかが入力されているか？
          const anyInputDone = hasTime || notifierSelected || contactSelected;
          // いずれかが入力されているのに、自分が空ならエラーを出す
          const timeError = anyInputDone && !hasTime;
          const notifierError = anyInputDone && !notifierSelected;
          const contactError = anyInputDone && !contactSelected;

          return (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={timeError}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={row.intake_stop_at ? dayjs(row.intake_stop_at, 'HH:mm') : null}
                      onChange={(newVal) => handleRowTimeChange(row.uuid, 'intake_stop_at', newVal, false)}
                      disabled={isDisabled || isSelectedDrainageContinue}
                      ampm={false}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: timeError,
                          sx: {
                            width: CONTACT_FIELD_WIDTH,
                            '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' },
                            '& .MuiInputBase-input': { padding: '8px 10px' }
                          }
                        }
                      }}
                    />
                    {timeError && (
                      <FormHelperText>時刻を入力してください。</FormHelperText>
                    )}
                  </LocalizationProvider>
                </FormControl>

                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={notifierError}>
                  <InputLabel id={`intake-stop-notifier-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>取水停止連絡者</InputLabel>
                  <Select
                    labelId={`intake-stop-notifier-label-${row.uuid}`}
                    value={row.intake_stop_notifier_person || ''}
                    label="取水停止連絡者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'intake_stop_notifier_person', e.target.value, false)}
                    disabled={isDisabled || isSelectedDrainageContinue}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value=""><em>-</em></MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>{u.name}</MenuItem>
                    ))}
                  </Select>
                  {notifierError && <FormHelperText>連絡者を選択してください。</FormHelperText>}
                </FormControl>

                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={contactError}>
                  <InputLabel id={`intake-stop-contact-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>取水停止連絡受信者</InputLabel>
                  <Select
                    labelId={`intake-stop-contact-label-${row.uuid}`}
                    value={row.intake_stop_contact_person || ''}
                    label="取水停止連絡受信者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'intake_stop_contact_person', e.target.value, false)}
                    disabled={isDisabled || isSelectedDrainageContinue}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value=""><em>-</em></MenuItem>
                    {filteredPartners.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>{u.person_name}</MenuItem>
                    ))}
                  </Select>
                  {contactError && <FormHelperText>連絡受信者を選択してください。</FormHelperText>}
                </FormControl>
              </div>
            </>
          );
        })()}
      </TableCell>

      <ArrowCell />

      {/* 取水停止完了 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.intake_stop_completed_at, isDisabled, isSelectedDrainageContinue) }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={row.intake_stop_completed_at ? dayjs(row.intake_stop_completed_at, 'HH:mm') : null}
            onChange={(newVal) => handleRowTimeChange(row.uuid, 'intake_stop_completed_at', newVal, false)}
            disabled={isDisabled || isSelectedDrainageContinue}
            ampm={false}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: 140,
                  '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' },
                  '& .MuiInputBase-input': { padding: '8px 10px' }
                }
              }
            }}
          />
        </LocalizationProvider>
      </TableCell>

      <ArrowCell />
      {/* 二シコーとの情報共有チャネルで都度更新（1回目） */}
      <TableCell />
      <ArrowCell />
      {/* 水力中給に情報共有 */}
      <TableCell />
      <ArrowCell />
      {/* 安全確認後（余水路の異常有無、入川者の有無）水路抜水 */}
      <TableCell />
      <ArrowCell />

      {/* 水路抜水連絡 */}
      <TableCell sx={{ minWidth: 180, maxWidth: 380, py: 0.5, backgroundColor: getCellBgColor(!!(row.drainage_notice_sent_at && row.drainage_notice_sent_person && row.drainage_notice_sent_user), isDisabled, isSelectedDrainageContinue || isSelectedDrainageIntakestop) }}>
        {(() => {
          const hasTime = !!row.drainage_notice_sent_at;
          const notifierSelected = !!row.drainage_notice_sent_person;
          const contactSelected = !!row.drainage_notice_sent_user;
          // いずれかが入力されているか？
          const anyInputDone = hasTime || notifierSelected || contactSelected;
          // いずれかが入力されているのに、自分が空ならエラーを出す
          const timeError = anyInputDone && !hasTime;
          const notifierError = anyInputDone && !notifierSelected;
          const contactError = anyInputDone && !contactSelected;

          return (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={timeError}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={row.drainage_notice_sent_at ? dayjs(row.drainage_notice_sent_at, 'HH:mm') : null}
                      onChange={(newVal) => handleRowTimeChange(row.uuid, 'drainage_notice_sent_at', newVal, false)}
                      disabled={isDisabled || isSelectedDrainageContinue || isSelectedDrainageIntakestop}
                      ampm={false}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: timeError,
                          sx: {
                            width: CONTACT_FIELD_WIDTH,
                            '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' },
                            '& .MuiInputBase-input': { padding: '8px 10px' }
                          }
                        }
                      }}
                    />
                    {timeError && (
                      <FormHelperText>時刻を入力してください。</FormHelperText>
                    )}
                  </LocalizationProvider>
                </FormControl>

                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={notifierError}>
                  <InputLabel id={`drainage-notice-sent-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路抜水連絡者</InputLabel>
                  <Select
                    labelId={`drainage-notice-sent-person-label-${row.uuid}`}
                    value={row.drainage_notice_sent_person || ''}
                    label="担当者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'drainage_notice_sent_person', e.target.value)}
                    disabled={isDisabled || isSelectedDrainageContinue || isSelectedDrainageIntakestop}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value=""><em>-</em></MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {notifierError && <FormHelperText>連絡者を選択してください。</FormHelperText>}
                </FormControl>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={contactError}>
                  <InputLabel id={`drainage-notice-sent-user-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路抜水連絡受信者</InputLabel>
                  <Select
                    labelId={`drainage-notice-sent-user-label-${row.uuid}`}
                    value={row.drainage_notice_sent_user || ''}
                    label="担当者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'drainage_notice_sent_user', e.target.value)}
                    disabled={isDisabled || isSelectedDrainageContinue || isSelectedDrainageIntakestop}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value=""><em>-</em></MenuItem>
                    {filteredPartners.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>
                        {u.person_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {contactError && <FormHelperText>連絡受信者を選択してください。</FormHelperText>}
                </FormControl>
              </div>
            </>
          );
        })()}
      </TableCell>

      <ArrowCell />

      {/* 水路抜水完了 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.drainage_completed_at, isDisabled, isSelectedDrainageContinue || isSelectedDrainageIntakestop) }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={row.drainage_completed_at ? dayjs(row.drainage_completed_at, 'HH:mm') : null}
            onChange={(newVal) => handleRowTimeChange(row.uuid, 'drainage_completed_at', newVal, false)}
            disabled={isDisabled || isSelectedDrainageContinue || isSelectedDrainageIntakestop}
            ampm={false}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: 140,
                  '& .MuiInputBase-root': {
                    height: 40,
                    boxSizing: 'border-box'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px'
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      </TableCell>

      <ArrowCell />

      {/* 水路内部点検実施箇所決定 */}
      <TableCell />
      <ArrowCell />
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_selected_inspection_locations, isDisabled, isSelectedDrainageContinue || isSelectedDrainageIntakestop)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_selected_inspection_locations}
              onChange={() => handleCheckboxChange(row.uuid, 'is_selected_inspection_locations', false)}
              disabled={isDisabled || isSelectedDrainageContinue || isSelectedDrainageIntakestop}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>
      <ArrowCell />

      {/* 水路内部点検者の決定 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!(row.canalinspector1 && row.canalinspector2), isDisabled, isSelectedDrainageContinue || isSelectedDrainageIntakestop) }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} >
            <InputLabel id={`canalinspector1-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路内部点検者(予定)</InputLabel>
            <Select
              labelId={`canalinspector1-${row.uuid}`}
              value={row.canalinspector1 || ''}
              label="水路内部点検者(予定)"
              onChange={(e) => handleRowSelectChange(row.uuid, 'canalinspector1', e.target.value, false)}
              disabled={isDisabled || isSelectedDrainageContinue || isSelectedDrainageIntakestop}
              sx={{ width: CONTACT_FIELD_WIDTH }}
            >
              <MenuItem value=""><em>-</em></MenuItem>
              {users.map((u) => (
                <MenuItem key={u.uuid} value={u.uuid}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} >
            <InputLabel id={`canalinspector2-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路内部点検者(予定)</InputLabel>
            <Select
              labelId={`canalinspector2-${row.uuid}`}
              value={row.canalinspector2 || ''}
              label="水路内部点検者(予定)"
              onChange={(e) => handleRowSelectChange(row.uuid, 'canalinspector2', e.target.value, false)}
              disabled={isDisabled || isSelectedDrainageContinue || isSelectedDrainageIntakestop}
              sx={{ width: CONTACT_FIELD_WIDTH }}
            >
              <MenuItem value=""><em>-</em></MenuItem>
              {users.map((u) => (
                <MenuItem key={u.uuid} value={u.uuid}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </TableCell>

      <ArrowCell />

      {/* 臨時点検の要請 */}
      <TableCell sx={{ minWidth: 200, maxWidth: 380, py: 0.5, backgroundColor: getCellBgColor(!!(row.request_emergency_inspection_at && row.request_emergency_inspection_person && row.request_emergency_inspection_user), isDisabled, false) }}>
        {(() => {
          const hasTime = !!row.request_emergency_inspection_at;
          const notifierSelected = !!row.request_emergency_inspection_person;
          const contactSelected = !!row.request_emergency_inspection_user;
          // いずれかが入力されているか？
          const anyInputDone = hasTime || notifierSelected || contactSelected;
          // いずれかが入力されているのに、自分が空ならエラーを出す
          const timeError = anyInputDone && !hasTime;
          const notifierError = anyInputDone && !notifierSelected;
          const contactError = anyInputDone && !contactSelected;

          return (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={timeError}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={row.request_emergency_inspection_at ? dayjs(row.request_emergency_inspection_at, 'HH:mm') : null}
                      onChange={(newVal) => handleRowTimeChange(row.uuid, 'request_emergency_inspection_at', newVal, false)}
                      disabled={isDisabled}
                      ampm={false}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: timeError,
                          sx: {
                            width: CONTACT_FIELD_WIDTH,
                            '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' },
                            '& .MuiInputBase-input': { padding: '8px 10px' }
                          }
                        }
                      }}
                    />
                    {timeError && (
                      <FormHelperText>時刻を入力してください。</FormHelperText>
                    )}
                  </LocalizationProvider>
                </FormControl>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={notifierError}>
                  <InputLabel id={`request-emergency-inspection-person-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>臨時点検要請連絡者</InputLabel>
                  <Select
                    labelId={`request-emergency-inspection-person-label-${row.uuid}`}
                    value={row.request_emergency_inspection_person || ''}
                    label="担当者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'request_emergency_inspection_person', e.target.value)}
                    disabled={isDisabled}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value="">
                      <em>-</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {notifierError && <FormHelperText>連絡者を選択してください。</FormHelperText>}
                </FormControl>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={contactError}>
                  <InputLabel id={`request-emergency-inspection-user-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }} >臨時点検要請連絡受信者</InputLabel>
                  <Select
                    labelId={`request-emergency-inspection-user-label-${row.uuid}`}
                    value={row.request_emergency_inspection_user || ''}
                    label="担当者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'request_emergency_inspection_user', e.target.value)}
                    disabled={isDisabled}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value="">
                      <em>-</em>
                    </MenuItem>
                    {filteredPartners.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>
                        {u.person_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {contactError && <FormHelperText>連絡受信者を選択してください。</FormHelperText>}
                </FormControl>
              </div>
            </>
          );
        })()}
      </TableCell>

      <ArrowCell />

      {/* 臨時点検完了 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.inspection_completed_at, isDisabled, false) }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={row.inspection_completed_at ? dayjs(row.inspection_completed_at, 'HH:mm') : null}
            onChange={(newVal) => handleRowTimeChange(row.uuid, 'inspection_completed_at', newVal, false)}
            disabled={isDisabled}
            ampm={false}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: 140,
                  '& .MuiInputBase-root': {
                    height: 40,
                    boxSizing: 'border-box'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px'
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      </TableCell>

      <ArrowCell />

      {/* 様式１の更新（1回目） */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_inspection_completed_update_form1, isDisabled, false)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_inspection_completed_update_form1}
              onChange={() => handleCheckboxChange(row.uuid, 'is_inspection_completed_update_form1', false)}
              disabled={isDisabled}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />

      {/* 臨時点検結果 */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(row.is_emergency_inspection !== null, isDisabled, false)}}>
        <FormControl size="small" fullWidth>
          <InputLabel id={`emergency-inspection-status-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>
            点検結果
          </InputLabel>
          <Select
            labelId={`emergency-inspection-status-label-${row.uuid}`}
            value={row.is_emergency_inspection === true ? 1 : (row.is_emergency_inspection === false ? 0 : "")}
            label="点検結果"
            onChange={(e) => {
              const val = e.target.value;
              const isAbnormal = val === 1 ? true : (val === 0 ? false : null);
              console.log('isAbnormal:', isAbnormal);
              handleRowSelectChange(row.uuid, 'is_emergency_inspection', isAbnormal);
            }}
            disabled={isDisabled}
            size="small"
            sx={{ width: 120 }}
          >
            <MenuItem value="">-</MenuItem>
            <MenuItem value={0}>異常なし</MenuItem>
            <MenuItem value={1}>異常あり</MenuItem>
          </Select>
        </FormControl>
      </CheckboxCell>

      <ArrowCell />

      {/* 管理職報告 */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_manager_report, isDisabled, isStepsAfterAbnormalDisabled)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_manager_report}
              onChange={() => handleCheckboxChange(row.uuid, 'is_manager_report', false)}
              size="small"
              disabled={isDisabled || isStepsAfterAbnormalDisabled}
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />

      {/* 水路抜水 (チェックボックス) */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_drainage, isDisabled, isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_drainage}
              onChange={() => handleCheckboxChange(row.uuid, 'is_drainage', false)}
              size="small"
              disabled={isDisabled || isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage}
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />

      {/* 水路抜水要請 */}
      <TableCell sx={{ minWidth: 180, maxWidth: 380, py: 0.5, backgroundColor: getCellBgColor(!!(row.drainage_request_at && row.drainage_request_person && row.drainage_request_user), isDisabled, isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage) }}>
        {(() => {
          const hasTime = !!row.drainage_request_at;
          const notifierSelected = !!row.drainage_request_person;
          const contactSelected = !!row.drainage_request_user;
          // いずれかが入力されているか？
          const anyInputDone = hasTime || notifierSelected || contactSelected;
          // いずれかが入力されているのに、自分が空ならエラーを出す
          const timeError = anyInputDone && !hasTime;
          const notifierError = anyInputDone && !notifierSelected;
          const contactError = anyInputDone && !contactSelected;

          return (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={timeError}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={row.drainage_request_at ? dayjs(row.drainage_request_at, 'HH:mm') : null}
                      onChange={(newVal) => handleRowTimeChange(row.uuid, 'drainage_request_at', newVal, false)}
                      ampm={false}
                      disabled={isDisabled || isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: timeError,
                          sx: {
                            width: CONTACT_FIELD_WIDTH,
                            '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' },
                            '& .MuiInputBase-input': { padding: '8px 10px' }
                          }
                        }
                      }}
                    />
                    {timeError && (
                      <FormHelperText>時刻を入力してください。</FormHelperText>
                    )}
                  </LocalizationProvider>
                </FormControl>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={notifierError}>
                  <InputLabel id={`drainage-request-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路抜水要請者</InputLabel>
                  <Select
                    labelId={`drainage-request-label-${row.uuid}`}
                    value={row.drainage_request_person || ''}
                    disabled={isDisabled || isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage}
                    label="水路抜水要請者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'drainage_request_person', e.target.value, false)}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value=""><em>-</em></MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>{u.name}</MenuItem>
                    ))}
                  </Select>
                  {notifierError && <FormHelperText>連絡者を選択してください。</FormHelperText>}
                </FormControl>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={contactError}>
                  <InputLabel id={`drainage-request-user-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路抜水要請担当者</InputLabel>
                  <Select
                    labelId={`drainage-request-user-label-${row.uuid}`}
                    value={row.drainage_request_user || ''}
                    disabled={isDisabled || isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage}
                    label="水路抜水要請担当者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'drainage_request_user', e.target.value, false)}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value=""><em>-</em></MenuItem>
                    {filteredPartners.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>{u.person_name}</MenuItem>
                    ))}
                  </Select>
                  {contactError && <FormHelperText>連絡受信者を選択してください。</FormHelperText>}
                </FormControl>
              </div>
            </>
          );
        })()}
      </TableCell>

      <ArrowCell />

      {/* 水路抜水時間 (2回目: TimePicker) */}
      {/* 注意: DDL定義に基づき is_drainage ではなく drainage_at を使用 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.drainage_at, isDisabled, isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage) }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={row.drainage_at ? dayjs(row.drainage_at, 'HH:mm') : null}
            onChange={(newVal) => handleRowTimeChange(row.uuid, 'drainage_at', newVal, false)}
            ampm={false}
            disabled={isDisabled || isStepsAfterAbnormalDisabled || isSelectedDrainageDrainage}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: 140,
                  '& .MuiInputBase-root': {
                    height: 40,
                    boxSizing: 'border-box'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px'
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      </TableCell>

      <ArrowCell />

      {/* 内部点検実施 */}
      <TableCell sx={{ minWidth: 200, maxWidth: 380, py: 0.5, backgroundColor: getCellBgColor(!!(row.perform_internal_inspection_user1 && row.perform_internal_inspection_user2), isDisabled, isDrainageAbnormal) }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} >
            <InputLabel id={`perform-internal-inspection_user1-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>内部点検実施者</InputLabel>
            <Select
              labelId={`perform-internal-inspection_user1-${row.uuid}`}
              value={row.perform_internal_inspection_user1 || ''}
              label="内部点検実施者"
              onChange={(e) => handleRowSelectChange(row.uuid, 'perform_internal_inspection_user1', e.target.value, false)}
              disabled={isDisabled || isDrainageAbnormal}
              sx={{ width: CONTACT_FIELD_WIDTH }}
            >
              <MenuItem value=""><em>-</em></MenuItem>
              {users.map((u) => (
                <MenuItem key={u.uuid} value={u.uuid}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} >
            <InputLabel id={`perform-internal-inspection_user2-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>内部点検実施者</InputLabel>
            <Select
              labelId={`perform-internal-inspection_user2-${row.uuid}`}
              value={row.perform_internal_inspection_user2 || ''}
              label="内部点検実施者"
              onChange={(e) => handleRowSelectChange(row.uuid, 'perform_internal_inspection_user2', e.target.value, false)}
              disabled={isDisabled || isDrainageAbnormal}
              sx={{ width: CONTACT_FIELD_WIDTH }}
            >
              <MenuItem value=""><em>-</em></MenuItem>
              {users.map((u) => (
                <MenuItem key={u.uuid} value={u.uuid}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </TableCell>

      <ArrowCell />

      {/* 内部点検完了 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.internal_inspection_completed_at, isDisabled, isDrainageAbnormal) }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={row.internal_inspection_completed_at ? dayjs(row.internal_inspection_completed_at, 'HH:mm') : null}
            onChange={(newVal) => handleRowTimeChange(row.uuid, 'internal_inspection_completed_at', newVal, false)}
            disabled={isDisabled || isDrainageAbnormal}
            ampm={false}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: 140,
                  '& .MuiInputBase-root': {
                    height: 40,
                    boxSizing: 'border-box'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px'
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      </TableCell>

      <ArrowCell />

      {/* 様式1の更新（2回目） */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_internal_inspection_update_form1, isDisabled, isDrainageAbnormal)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_internal_inspection_update_form1}
              disabled={isDisabled || isDrainageAbnormal}
              onChange={() => handleCheckboxChange(row.uuid, 'is_internal_inspection_update_form1', false)}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />

      {/* 発電再開箇所決定 */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_decide_restart_locations, isDisabled, isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_decide_restart_locations}
              disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
              onChange={() => handleCheckboxChange(row.uuid, 'is_decide_restart_locations', false)}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />

      {/* 灌漑利水者へ連絡 (2回目) */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_notified_irrigation_user2, isDisabled, isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_notified_irrigation_user2}
              disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
              onChange={() => handleCheckboxChange(row.uuid, 'is_notified_irrigation_user2', false)}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />
      {/* 水力中給に情報共有 (2回目) */}
      <TableCell />
      <ArrowCell />

      {/* 水路充水要請 */}
      <TableCell sx={{ minWidth: 200, maxWidth: 380, py: 0.5, backgroundColor: getCellBgColor(!!(row.request_waterway_refill_at && row.request_waterway_refill_person && row.request_waterway_refill_user), isDisabled, isSelectedDrainageContinue && isStepsAfterAbnormalDisabled) }}>
        {(() => {
          const hasTime = !!row.request_waterway_refill_at;
          const notifierSelected = !!row.request_waterway_refill_person;
          const contactSelected = !!row.request_waterway_refill_user;
          // いずれかが入力されているか？
          const anyInputDone = hasTime || notifierSelected || contactSelected;
          // いずれかが入力されているのに、自分が空ならエラーを出す
          const timeError = anyInputDone && !hasTime;
          const notifierError = anyInputDone && !notifierSelected;
          const contactError = anyInputDone && !contactSelected;

          return (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={timeError}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={row.request_waterway_refill_at ? dayjs(row.request_waterway_refill_at, 'HH:mm') : null}
                      onChange={(newVal) => handleRowTimeChange(row.uuid, 'request_waterway_refill_at', newVal, false)}
                      disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
                      ampm={false}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: timeError,
                          sx: {
                            width: CONTACT_FIELD_WIDTH,
                            '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' },
                            '& .MuiInputBase-input': { padding: '8px 10px' }
                          }
                        }
                      }}
                    />
                    {timeError && (
                      <FormHelperText>時刻を入力してください。</FormHelperText>
                    )}
                  </LocalizationProvider>
                </FormControl>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={notifierError}>
                  <InputLabel id={`request-water-refill-person-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路充水要請連絡者</InputLabel>
                  <Select
                    labelId={`request-water-refill-person-label-${row.uuid}`}
                    value={row.request_waterway_refill_person || ''}
                    label="担当者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'request_waterway_refill_person', e.target.value)}
                    disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value="">
                      <em>-</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {notifierError && <FormHelperText>連絡者を選択してください。</FormHelperText>}
                </FormControl>
                <FormControl size="small" sx={{ width: CONTACT_FIELD_WIDTH }} error={contactError}>
                  <InputLabel id={`request-water-refill-user-label-${row.uuid}`} sx={{ fontSize: '0.8rem' }}>水路充水要請連絡受信者</InputLabel>
                  <Select
                    labelId={`request-water-refill-user-label-${row.uuid}`}
                    value={row.request_waterway_refill_user || ''}
                    label="担当者"
                    onChange={(e) => handleRowSelectChange(row.uuid, 'request_waterway_refill_user', e.target.value)}
                    disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
                    sx={{ width: CONTACT_FIELD_WIDTH }}
                  >
                    <MenuItem value="">
                      <em>-</em>
                    </MenuItem>
                    {filteredPartners.map((u) => (
                      <MenuItem key={u.uuid} value={u.uuid}>
                        {u.person_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {contactError && <FormHelperText>連絡受信者を選択してください。</FormHelperText>}
                </FormControl>
              </div>
            </>
          );
        })()}
      </TableCell>

      <ArrowCell />

      {/* 充水完了 */}
      {/* <TableCell sx={{ py: 0.5, backgroundColor: (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled) ? '#eeeeee' : 'transparent' }}> */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.refill_completed_at, isDisabled, isSelectedDrainageContinue && isStepsAfterAbnormalDisabled) }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={row.refill_completed_at ? dayjs(row.refill_completed_at, 'HH:mm') : null}
            disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
            onChange={(newVal) => handleRowTimeChange(row.uuid, 'refill_completed_at', newVal, false)}
            ampm={false}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: 140,
                  '& .MuiInputBase-root': {
                    height: 40,
                    boxSizing: 'border-box'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px'
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      </TableCell>

      <ArrowCell />

      {/* G長報告 */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_report_to_g_manager, isDisabled, isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_report_to_g_manager}
              disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
              onChange={() => handleCheckboxChange(row.uuid, 'is_report_to_g_manager', false)}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />

      {/* 発電再開 */}
      <TableCell sx={{ py: 0.5, backgroundColor: getCellBgColor(!!row.restart_power_generation_at, isDisabled, isSelectedDrainageContinue && isStepsAfterAbnormalDisabled) }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={row.restart_power_generation_at ? dayjs(row.restart_power_generation_at, 'HH:mm') : null}
            disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
            onChange={(newVal) => handleRowTimeChange(row.uuid, 'restart_power_generation_at', newVal, false)}
            ampm={false}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: 140,
                  '& .MuiInputBase-root': {
                    height: 40,
                    boxSizing: 'border-box'
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px'
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      </TableCell>

      <ArrowCell />

      {/* 灌漑利水者へ連絡 (3回目) */}
      <CheckboxCell sx={{backgroundColor: getCellBgColor(!!row.is_notified_irrigation_user3, isDisabled, isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}}>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_notified_irrigation_user3}
              disabled={isDisabled || (isSelectedDrainageContinue && isStepsAfterAbnormalDisabled)}
              onChange={() => handleCheckboxChange(row.uuid, 'is_notified_irrigation_user3', false)}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>

      <ArrowCell />

      {/* 完了 */}
      <CheckboxCell>
        <FormControlLabel
          control={
            <Checkbox
              checked={row.is_completed}
              onChange={() => handleCheckboxChange(row.uuid, 'is_completed', false)}
              disabled={isDisabled}
              size="small"
            />
          }
          label=""
        />
      </CheckboxCell>
    </TableRow>
  );
};